import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Box } from "@mui/material";
import Loader from "@components/Loader/Loader";
import FamilyInfoForm from "@components/FamilyInfoForm/FamilyInfoForm";
import { useForm } from "react-hook-form";
import {
  FamilyMemberFormType,
  PatientFormType,
  patientToFormType,
} from "@utils/patientFormHelper";
import { Patient } from "@models/patient";
import { setMessage } from "@store/snackbarSlice";
import { useUpdatePatientMutation } from "@store/patientApi";
import FamilyMemberFormDrawer from "@components/Home/FamilyMembers/FamilyMemberFormDrawer";
import { useGetCharactersQuery } from "@store/characterApi";

const FamilyMembers: FC = () => {
  const { t } = useTranslation();
  const patient = useSelector((state: RootState) => state.home.selectedPatient);
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const [updatePatient] = useUpdatePatientMutation();
  const dispatch = useDispatch();
  const [selectedFamilyMember, setSelectedFamilyMember] =
    useState<FamilyMemberFormType | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data: characters } = useGetCharactersQuery(
    {
      patientId: patientId!,
    },
    { skip: !patientId },
  );

  const form = useForm<PatientFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });

  const { reset, getValues, clearErrors, handleSubmit } = form;

  useEffect(() => {
    if (patient) {
      reset(patientToFormType(patient));
    }
  }, [patient, reset]);

  const save = async () => {
    const values = getValues();
    try {
      const { data, error } = await updatePatient({
        ...values,
        id: patient?.id,
      } as unknown as Patient);
      if (data && !error) {
        dispatch(
          setMessage({
            content: t(`familyMembers.updateSuccessMessage`),
            severity: "success",
          }),
        );
      } else {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  const onAdd = useCallback(() => {
    setSelectedFamilyMember(null);

    setDrawerOpen(true);
  }, []);

  const onEdit = useCallback((familyMember: FamilyMemberFormType) => {
    setSelectedFamilyMember(familyMember);
    setDrawerOpen(true);
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      {!patient ? (
        <Loader />
      ) : (
        <>
          <form
            onSubmit={(e) => {
              clearErrors();
              return handleSubmit(save)(e);
            }}
          >
            <Box maxWidth="1100px">
              <FamilyInfoForm
                form={form}
                onAdd={onAdd}
                onEdit={onEdit}
                onSave={save}
                characters={characters?.data || []}
              />
            </Box>
          </form>

          <FamilyMemberFormDrawer
            open={drawerOpen}
            familyMember={selectedFamilyMember}
            patient={patient}
            onClose={() => setDrawerOpen(false)}
          />
        </>
      )}
    </Box>
  );
};

export default FamilyMembers;
